/**
 * VoiceService
 *
 * Handles TTS provider availability, voice selection, and provider defaults.
 * All methods are stateless and pure.
 *
 * Extracted from: VideoConfigForm.tsx (lines 135-184, 442-467)
 */

import type { Language, TTSProvider } from '../types';
import type { VoiceOption } from '../constants';
import { TTS_LANGUAGE_SUPPORT, getVoicesForLanguageAndProvider } from '../constants';

// Exact mapping from NLLB codes to TTS voice codes
const NLLB_TO_TTS_CODE_MAP: Record<string, string> = {
  en: 'en-us',
  pt: 'pt-br',
  zh: 'cmn',
  // Other codes are already exact matches (es, fr, it, ja, hi, etc.)
};

export class VoiceService {
  /**
   * Checks if a TTS provider supports the selected language
   *
   * @param provider - TTS provider ID (e.g., "kokoro", "google", "pyttsx3")
   * @param language - Language code (e.g., "en-us", "pt-br")
   * @returns true if provider supports language AND has voices available, false otherwise
   *
   * @example
   * isProviderAvailableForLanguage("kokoro", "en-us") // Returns true
   * isProviderAvailableForLanguage("kokoro", "zh-cn") // Returns false
   * isProviderAvailableForLanguage("pyttsx3", "any-lang") // Returns true (auto)
   */
  static isProviderAvailableForLanguage(provider: string, language: string): boolean {
    // Guard against undefined or null provider
    if (!provider) return false;

    // Google TTS always has voices (generic male/female)
    if (provider === 'google') {
      return true;
    }

    // PyTTSx3 has "auto" as value - allow all languages (system-dependent)
    if (provider === 'pyttsx3') {
      return true;
    }

    const supportedLanguages = TTS_LANGUAGE_SUPPORT[provider];
    if (!supportedLanguages) return true; // If no restrictions defined, assume available

    // For providers with explicit language lists, check if language is supported
    let languageSupported = false;
    if (Array.isArray(supportedLanguages) && supportedLanguages.includes) {
      // Direct match (e.g., 'en-us' matches 'en-us')
      if (supportedLanguages.includes(language)) {
        languageSupported = true;
      } else {
        // Fuzzy match for language variants (e.g., 'en' matches 'en-us', 'en-gb')
        // Extract base language code (e.g., 'en' from 'en-us')
        const baseLanguage = language.split('-')[0];
        languageSupported = supportedLanguages.some(
          (supportedLang) =>
            supportedLang.startsWith(baseLanguage + '-') || supportedLang === baseLanguage
        );
      }
    } else {
      // If not array-based, assume supported
      languageSupported = true;
    }

    // Even if language is in the support list, verify voices actually exist
    // This handles cases where TTS_LANGUAGE_SUPPORT lists a language but
    // TTS_VOICES_BY_LANGUAGE_AND_PROVIDER has no voices for that combination
    if (languageSupported) {
      const voices = this.getAvailableVoices(language as Language, provider as TTSProvider);
      return voices.length > 0;
    }

    return false;
  }

  /**
   * Gets the default TTS provider for a given language
   * Priority: Kokoro > Google TTS > pyttsx3
   * ALWAYS returns a provider - never returns null
   *
   * @param language - Language code
   * @returns Default provider ID (guaranteed to return a value)
   *
   * @example
   * getDefaultProviderForLanguage("en-us") // Returns "kokoro"
   * getDefaultProviderForLanguage("de") // Returns "google" (Kokoro doesn't support German)
   */
  static getDefaultProviderForLanguage(language: string): string {
    // Priority 1: Kokoro (if available for language)
    if (this.isProviderAvailableForLanguage('kokoro', language)) {
      return 'kokoro';
    }

    // Priority 2: Google TTS (if available for language)
    if (this.isProviderAvailableForLanguage('google', language)) {
      return 'google';
    }

    // Priority 3: pyttsx3 (supports all languages)
    return 'pyttsx3';
  }

  /**
   * Gets available voices for a given language and provider
   *
   * @param language - Language code (exact match required)
   * @param provider - TTS provider ID
   * @returns Array of voice options for the language/provider combination
   */
  static getAvailableVoices(language: Language, provider: TTSProvider): VoiceOption[] {
    // Google TTS (gtts library) only ships a single neural voice per language — there
    // is no male variant. Exposing two options made the male preview play female audio
    // because the backend renders identical audio for both voice IDs.
    if (provider === 'google') {
      return [
        {
          id: 'google-female',
          name: 'Google Voice',
          gender: 'female' as const,
          language: language,
          provider: 'google' as const,
        },
      ];
    }

    // Normalize language code (NLLB → TTS)
    const normalizedLanguage = (NLLB_TO_TTS_CODE_MAP[language] || language) as Language;

    // Get voices for exact normalized code
    return getVoicesForLanguageAndProvider(normalizedLanguage, provider);
  }

  /**
   * Gets the default voice for a given language and provider
   *
   * @param language - Language code
   * @param provider - TTS provider ID
   * @returns Default voice ID (first available voice)
   */
  static getDefaultVoice(language: Language, provider: TTSProvider): string {
    if (provider === 'google') {
      return 'google-female';
    }

    const availableVoices = this.getAvailableVoices(language, provider);
    return availableVoices[0]?.id || 'google-female';
  }
}
